import { Divider, TextField, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { FieldErrors } from 'react-hook-form';

import Button from '@/components/Button';
import useResponsive from '@/hooks/useResponsive';
import { ProductResponse } from '@/providers/HttpProvider/Fulfill/Inventory/types';
import { Product } from '@/types/fulfill/inventory';

import { SendToWarehouseFormData } from '../../inventorySchema';
import WarehouseSelector from '../../WarehouseSelector';

export interface AddUnitsProps {
  step: number;
  setCurrentStep: (step: number) => void;
  products?: ProductResponse['products'];
  productItems?: { sku: string; quantity: number }[];
  onChange: ({ index, sku, quantity }: { index: number; sku: string; quantity: string }) => void;
  errors?: FieldErrors<SendToWarehouseFormData>;
  isDisabled?: boolean;
  editStep: boolean;
  warehouse?: string;
  onWarehouseChange?: (value: string) => void;
  warehouseOptions?: { value: string; label: string }[];
  isLoadingWarehouses?: boolean;
  requiresHomePickup?: boolean;
}

const AddUnits = ({
  step,
  setCurrentStep,
  products = [],
  productItems = [],
  errors,
  onChange,
  isDisabled,
  editStep,
  warehouse = '',
  onWarehouseChange,
  warehouseOptions = [],
  isLoadingWarehouses = false,
  requiresHomePickup = false,
}: AddUnitsProps) => {
  const theme = useTheme();
  const isSmallDrawer = useResponsive('between', 'lg', 'xl');
  const isPhone = useResponsive('down', 'sm');

  const isStacked = isSmallDrawer || isPhone;
  const totalVolume = products.reduce((acc, product, index) => {
    const quantity = productItems[index]?.quantity ?? 0;
    if (product.dimensions) {
      const { length, width, height } = product.dimensions;
      const volumeInCm3 = length * width * height * quantity;
      return acc + volumeInCm3;
    }
    return acc;
  }, 0);
  const totalVolumeInCm3 = totalVolume.toFixed(0);
  const totalUnits = productItems.reduce((acc, cur) => acc + (cur.quantity || 0), 0);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: isStacked ? 'column' : 'row',
          gap: '1rem',
          my: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ minWidth: '4.1rem' }}>
          Paso <span style={{ color: theme.palette.primary.main }}>1</span>
        </Typography>
        <Divider orientation={isStacked ? 'horizontal' : 'vertical'} flexItem />
        <Typography variant="subtitle2">Indica cuántas unidades vas a enviar</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid
          container
          size={{ xs: 12 }}
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            padding: theme.spacing(2),
          }}
        >
          <Typography variant="body1" color={theme.palette.grey[500]}>
            Producto
          </Typography>
          <Typography variant="body1" color={theme.palette.grey[500]}>
            Unidades
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Grid
            container
            sx={{
              flexDirection: 'column',
              border: '1px solid',
              borderColor: theme.palette.grey[300],
              borderRadius: '1rem 1rem 0 0',
              p: 2,
            }}
          >
            {products.map((product, index) => (
              <ProductsBox
                key={product.sku}
                product={product}
                index={index}
                value={productItems?.[index]?.quantity}
                onChange={onChange}
                errors={errors?.products?.[index]?.quantity?.message}
                name={`products[${index}].quantity`}
              />
            ))}
          </Grid>

          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              gap: '0.5rem',
              alignItems: 'center',
              border: '1px solid',
              borderColor: theme.palette.grey[300],
              borderTop: '1px solid',
              borderTopColor: theme.palette.grey[400],
              borderRadius: '0 0 1rem 1rem',
              padding: theme.spacing(2),
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" fontWeight="bold">
                Producto{products.length !== 1 ? 's' : ''}: {products.length}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                Volumen estimado: {totalVolumeInCm3} cm³
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="bold" color={theme.palette.primary.main}>
              Total de unidades: {totalUnits}
            </Typography>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <WarehouseSelector
            label="Almacén"
            value={warehouse}
            onChange={(value) => onWarehouseChange?.(value)}
            placeholder="Seleccione un almacén"
            title={
              <>
                ¿<span style={{ color: theme.palette.primary.main }}>A qué almacén</span> deseas
                enviar?
              </>
            }
            infoText="Seleccione el almacén de destino donde desea enviar sus productos."
            error={errors?.warehouse?.message}
            options={warehouseOptions}
            isLoading={isLoadingWarehouses}
          />
        </Grid>

        <Grid container size={{ xs: 12 }} sx={{ mt: 2, justifyContent: 'flex-end' }}>
          <Button
            disabled={isDisabled}
            onClick={() => setCurrentStep(editStep ? (requiresHomePickup ? 5 : 4) : step + 1)}
          >
            Continuar
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddUnits;

interface ProductsBoxProps {
  product: Product;
  index: number;
  errors?: string;
  value?: number;
  onChange?: ({ index, sku, quantity }: { index: number; sku: string; quantity: string }) => void;
  name: string;
}

export const ProductsBox = ({
  product,
  index,
  errors,
  onChange,
  name,
  value,
}: ProductsBoxProps) => {
  const theme = useTheme();
  const isBetweenXXSAndMD = useResponsive('between', 'xxs', 'md');
  const isBetweenLGAndXL = useResponsive('between', 'lg', 'xl');
  const isHorizontal = isBetweenXXSAndMD || isBetweenLGAndXL;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xxs: '1fr', md: '1fr auto 200px', lg: '1fr', xl: '1fr auto 200px' },
        gap: 2,
        alignItems: 'start',
        marginBottom: 2,
        '&:last-child': {
          marginBottom: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            minWidth: 60,
            minHeight: 60,
            flexShrink: 0,
            borderRadius: '1rem',
            overflow: 'hidden',
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={product.thumbnail}
            alt={`thumbnail-${product.sku}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
            }}
          >
            {product.name}
          </Typography>
          <Typography
            variant="body2"
            color={theme.palette.primary.main}
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
            }}
          >
            SKU <strong>{product.sku}</strong>
          </Typography>
        </Box>
      </Box>

      <Divider
        orientation={isHorizontal ? 'horizontal' : 'vertical'}
        flexItem
        sx={{
          display: { xxs: 'block', md: 'block', lg: 'block', xl: 'block' },
          mx: isHorizontal ? 0 : 0,
          my: isHorizontal ? 1 : 0,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <TextField
          type="number"
          variant="standard"
          placeholder="Unidades"
          sx={{ width: '80%', '&& input': { textAlign: 'center' } }}
          value={Number.isFinite(value) ? value : ''}
          name={name}
          onChange={(e) =>
            onChange?.({
              index,
              sku: product.sku,
              quantity: e.target.value,
            })
          }
          helperText={errors}
          error={Boolean(errors)}
        />
      </Box>
    </Box>
  );
};
